import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
    useTheme,
    MenuItem,
    FormControl,
    Grid,
    Button,
    Typography,
    TextField,
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { MuiThemeProvider } from "@material-ui/core/styles";

import Input from "../../Components/Inputs/Input";
import InputMask from "../../Components/Inputs/InputMask";

import { setLoadingGeral } from "../../Store/Actions//Reducer/mainActions";
import { setOpenMenu, setOpenSeguradoForm, setUltimoUpdateListaSegurados, setUltimoUpdateNit } from "../../Store/Actions/Reducer/menuActions";
import { buscarSeguradoPorCPF, cadastrarSegurado, editarSegurado } from "../../Store/Actions/API/seguradosActions";
import { validarCPF, retirarFormatacaoCPF, removeCharactersAndSpecialCharacter } from "../../Utils/utils";
import temaFormulario from "../../Styles/Temas/temaFormulario";
import { useStyles } from "./styles";

const rawSegurado = {
    nome: '',
    cpf: '',
    nit: '',
    dataNascimento: '',
    tipoConta: '',
    numeroBanco: '',
    numeroAgencia: '',
    numeroConta: '',
    digitoVerificador: ''
}

export default function ModalFormSegurado(props) {
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useDispatch();

    const { openSeguradoForm, cpfSeguradoSelecionado } = useSelector((state) => state.menu);
    const { isLoading } = useSelector((state) => state.main);

    const [segurado, setSegurado] = useState({ ...rawSegurado });
    const [nitInicial, setNitInicial] = useState(null);
    const [erroCpf, setErroCpf] = useState('');
    const [erroNome, setErroNome] = useState('');
    const [erroNit, setErroNit] = useState('');
    const [erroDataNascimento, setErroDataNascimento] = useState('');

    useEffect(() => {
        if (cpfSeguradoSelecionado !== segurado.cpf) buscarSegurado();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSeguradoForm]);

    const buscarSegurado = async () => {
        if (!cpfSeguradoSelecionado) return;

        dispatch(setLoadingGeral(true, "Buscando segurado"));

        let seguradoTemp = await buscarSeguradoPorCPF(cpfSeguradoSelecionado);

        dispatch(setLoadingGeral(false));

        if (seguradoTemp === null) {
            dispatch(setOpenSeguradoForm(false));
            return;
        }

        setNitInicial(seguradoTemp.nit ?? null);

        setSegurado({ ...seguradoTemp, dataNascimento: seguradoTemp.dataNascimento ? 
            moment(seguradoTemp.dataNascimento).format('DD/MM/YYYY').toString() : null });
    }

    const redirectTo = (path, cpf) => {
        if (window.location.pathname !== path) {
            history.push(path, { cpf });
        }
    }

    const onChangeNome = (event) => {        
        if (/^[a-zA-Z\u00C0-\u00FF ]*$/.test(event.target.value) || event.target.value === '') {
            setSegurado({ ...segurado, nome: event.target.value });
        }
    }    

    const onChangeNumeroBanco = (event) => {
        if (/^[0-9]*$/.test(event.target.value) || event.target.value === '') {
            setSegurado({ ...segurado, numeroBanco: event.target.value });
        }
    }

    const onChangeNumeroAgencia = (event) => {
        if (/^[0-9]*$/.test(event.target.value) || event.target.value === '') {
            setSegurado({ ...segurado, numeroAgencia: event.target.value });
        }
    }

    const onChangeNumeroConta = (event) => {
        if (/^[0-9]*$/.test(event.target.value) || event.target.value === '') {
            setSegurado({ ...segurado, numeroConta: event.target.value });
        }
    }

    const onChangeDigitoVerificador = (event) => {
        if (/^[0-9]*$/.test(event.target.value) || event.target.value === '') {
            setSegurado({ ...segurado, digitoVerificador: event.target.value });
        }
    }

    const cancelar = () => {
        dispatch(setOpenSeguradoForm(false));
        setErroCpf('');
        setErroNome('');
        setSegurado({ ...rawSegurado });
    }

    const validar = () => {
        let valido = true;

        if (!segurado.nome || (segurado.nome && segurado.nome.trim().length<3)) {
            setErroNome('Este campo é de preenchimento obrigatório.');
            valido = false;
        }

        if (!segurado.cpf || (segurado.cpf && removeCharactersAndSpecialCharacter(segurado.cpf).length<11)) {
            setErroCpf('Este campo é de preenchimento obrigatório.');
            valido = false;
        }

        if (!valido) {
            toast.error('Verifique o preenchimento do(s) campo(s) obrigatório(s) e tente novamente.')
        }

        if (valido && !validarCPF(segurado.cpf)) {
            toast.error('O CPF informado é inválido. Verifique os dados e tente novamente.')
            setErroCpf('CPF inválido.');
            valido = false;
        }

        if (segurado.nit && (removeCharactersAndSpecialCharacter(segurado.nit).length > 0 && removeCharactersAndSpecialCharacter(segurado.nit).length < 11)) {
            setErroNit('Insira um NIT válido.');
            valido = false;
        }

        if (segurado.dataNascimento && segurado.dataNascimento.replace(/_/g, '').length > 0) {
            let dataTmp = segurado.dataNascimento.replace(/_/g, '');
            let momentNascimento = moment(segurado.dataNascimento, 'DD/MM/YYYY');
            let dataValida = dataTmp.length === 10 && momentNascimento.isValid() && momentNascimento.isAfter(moment().subtract(150, 'years'));
            if (!dataValida) {
                setErroDataNascimento('Insira uma data válida.');
                valido = false;
            }
        }

        return valido;
    }

    const clicaProsseguir = async () => {
        if (!validar()) return;

        let seguradoTemp = Object.assign({}, segurado)
        seguradoTemp.cpf = retirarFormatacaoCPF(seguradoTemp.cpf);
        // Verifica se as strings não são vazias e passa null
        seguradoTemp.nit = (seguradoTemp.nit && removeCharactersAndSpecialCharacter(seguradoTemp.nit).length > 0) ?
            removeCharactersAndSpecialCharacter(seguradoTemp.nit) :
            null;
        seguradoTemp.dataNascimento = (seguradoTemp.dataNascimento && seguradoTemp.dataNascimento.replace(/_/g, '').length > 0) ?
            moment(seguradoTemp.dataNascimento, 'DD/MM/YYYY').toDate() :
            null;
        seguradoTemp.tipoConta = (seguradoTemp.tipoConta && seguradoTemp.tipoConta.length > 0) ? seguradoTemp.tipoConta : null;
        seguradoTemp.numeroBanco = (seguradoTemp.numeroBanco && seguradoTemp.numeroBanco.length > 0) ? seguradoTemp.numeroBanco : null;
        seguradoTemp.numeroAgencia = (seguradoTemp.numeroAgencia && seguradoTemp.numeroAgencia.length > 0) ? seguradoTemp.numeroAgencia : null;
        seguradoTemp.numeroConta = (seguradoTemp.numeroConta && seguradoTemp.numeroConta.length > 0) ? seguradoTemp.numeroConta : null;
        seguradoTemp.digitoVerificador = (seguradoTemp.digitoVerificador && seguradoTemp.digitoVerificador.length > 0) ? seguradoTemp.digitoVerificador : null;
        const seguradoEnviar = seguradoTemp;

        if (!cpfSeguradoSelecionado) await confirmaCadastrar(seguradoEnviar);
        else await confirmaEditar(seguradoEnviar);
    }

    const confirmaCadastrar = async (seguradoEnviar) => {
        dispatch(setOpenSeguradoForm(false));
        dispatch(setLoadingGeral(true, "Cadastrando segurado..."));

        const response = await cadastrarSegurado(seguradoEnviar);

        dispatch(setLoadingGeral(false));

        if (response.sucesso) {
            dispatch(setUltimoUpdateListaSegurados());
            dispatch(setOpenMenu(false));
            setSegurado({ ...rawSegurado });
            redirectTo(
                `/segurado/${retirarFormatacaoCPF(seguradoEnviar.cpf)}`,
                segurado.cpf
            );
        } else {
            if (response.mensagem && response.mensagem === 'CPF já cadastrado.') {
                setSegurado({ ...rawSegurado });
                dispatch(setOpenMenu(false));
                redirectTo(
                    `/segurado/${retirarFormatacaoCPF(seguradoEnviar.cpf)}`,
                    segurado.cpf
                );
            } else {
                dispatch(setOpenSeguradoForm(true));
            }
        }
    }

    const confirmaEditar = async (seguradoEnviar) => {
        dispatch(setOpenSeguradoForm(false));
        dispatch(setLoadingGeral(true, "Editando segurado..."));

        const response = await editarSegurado(seguradoEnviar);

        dispatch(setLoadingGeral(false));

        if (response.sucesso) {
            dispatch(setUltimoUpdateListaSegurados());
            if(seguradoEnviar.nit !== nitInicial){
                dispatch(setUltimoUpdateNit());
            }
            setSegurado({ ...rawSegurado });
            setNitInicial(null);
            dispatch(setOpenMenu(false));
            redirectTo(
                `/segurado/${retirarFormatacaoCPF(seguradoEnviar.cpf)}`,
                segurado.cpf
            );
        } else {
            dispatch(setOpenSeguradoForm(true, seguradoEnviar.cpf));
        }
    }

    return (
        <MuiThemeProvider theme={temaFormulario}>
            <Dialog
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
                open={(openSeguradoForm && !isLoading)}
                onClose={cancelar}
                aria-labelledby="modal-segurado"
                className={classes.left}
            >
                <DialogTitle id="modal-segurado">
                    {cpfSeguradoSelecionado ? "Editar Segurado" : "Cadastrar Segurado"}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={9}>
                            <Input
                                id="input_nome"
                                required
                                label="Nome"
                                color='secondary'
                                maxLength={60}
                                value={segurado.nome || ''}
                                setValue={onChangeNome}
                                erro={erroNome}
                                setErro={() => setErroNome('')}
                                className={classes.input}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <InputMask
                                id="input_cpf"
                                required
                                label="CPF"
                                color='secondary'
                                mask="999.999.999-99"
                                placeholder="___.___.___-__"
                                value={segurado.cpf || ''}
                                setValue={(e) =>
                                    setSegurado({ ...segurado, cpf: e.target.value })
                                }
                                erro={erroCpf}
                                setErro={() => setErroCpf('')}
                                className={classes.input}
                                disabled={cpfSeguradoSelecionado ? true : false}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <InputMask
                                id="input_nit"
                                label="NIT"
                                color='secondary'
                                mask="999.99999.99-9"
                                placeholder="___._____.__-_"
                                value={segurado.nit || ''}
                                erro={erroNit}
                                setErro={() => setErroNit('')}
                                setValue={(e) =>
                                    setSegurado({ ...segurado, nit: e.target.value })
                                }
                                className={classes.input}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <InputMask
                                id="input_data_nascimento"
                                label="Data de nascimento"
                                mask="99/99/9999"
                                color='secondary'
                                placeholder="__/__/____"
                                erro={erroDataNascimento}
                                setErro={() => setErroDataNascimento('')}
                                value={segurado.dataNascimento || ''}
                                setValue={(e) =>
                                    setSegurado({ ...segurado, dataNascimento: e.target.value })
                                }
                                className={classes.input}
                            />
                        </Grid>
                    </Grid>
                    <Typography className={classes.divDadosBancarios}>
                        Dados Bancários (para crédito)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <FormControl fullWidth className={classes.input}>                                
                                <TextField
                                    select
                                    id="select_tipoConta"
                                    label="Tipo da conta"
                                    color='secondary'
                                    value={segurado.tipoConta || ""}
                                    InputLabelProps={{shrink: true}}
                                    onChange={(e) =>
                                        setSegurado({ ...segurado, tipoConta: e.target.value })
                                    }
                                >
                                    <MenuItem style={{minHeight: 30}} value={""}></MenuItem>
                                    <MenuItem value={"Conta Poupança"}>Conta Poupança</MenuItem>
                                    <MenuItem value={"Conta Corrente"}>Conta Corrente</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                            <Input
                                color='secondary'
                                id="input_numero_banco"
                                label="Nº Banco"
                                maxLength={3}
                                value={segurado.numeroBanco || ''}
                                setValue={onChangeNumeroBanco}
                                className={classes.input}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Input
                                color='secondary'
                                id="input_numero_agencia"
                                label="Nº Agência"
                                maxLength={4}
                                value={segurado.numeroAgencia || ''}
                                setValue={onChangeNumeroAgencia}
                                className={classes.input}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Input
                                color='secondary'
                                id="input_numero_conta"
                                label="Nº Conta"
                                maxLength={10}
                                value={segurado.numeroConta || ''}
                                setValue={onChangeNumeroConta}
                                className={classes.input}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <Input
                                color='secondary'
                                id="input_conta_dv"
                                label="DV"
                                maxLength={2}
                                value={segurado.digitoVerificador || ''}
                                setValue={onChangeDigitoVerificador}
                                className={classes.input}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        id="button_cancelar"
                        onClick={cancelar}
                        className={classes.button}
                    >
                        CANCELAR
                    </Button>
                    <Button
                        id="button_confirmar_modal_segurado"
                        variant="contained"
                        color="primary"
                        onClick={clicaProsseguir}
                        className={classes.button}
                        startIcon={<SaveIcon />}
                    >
                        {cpfSeguradoSelecionado ? "EDITAR" : "CADASTRAR"}
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiThemeProvider>
    );
}
